import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** On/off toggle (role="switch") — for settings that take effect as a whole, e.g. "widget live". */
export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
  size = 'md',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const id = useId();
  const track = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11';
  const knob = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const shift = size === 'sm' ? 'translate-x-4' : 'translate-x-5';
  return (
    <div className={cn('flex items-start gap-3', className)}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'focus-visible:ring-brand-500/30 relative inline-flex shrink-0 items-center rounded-full transition-colors focus-visible:ring-4 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          track,
          checked ? 'bg-brand-600' : 'bg-slate-200',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'inline-block translate-x-0.5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform',
            knob,
            checked && shift,
          )}
        />
      </button>
      {(label || description) && (
        <label htmlFor={id} className="min-w-0 cursor-pointer text-sm">
          {label && <span className="block font-medium text-slate-800">{label}</span>}
          {description && <span className="block text-xs text-slate-500">{description}</span>}
        </label>
      )}
    </div>
  );
}
